// Templates: several wordings of the same message so the carrier never sees a repeat.
// Phase 2: a category (First message / Reminder / Other) and filter chips.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { renderedWorstCase } from '@bt/core';
import { api, type Template, type TemplateCategory } from '../../api';
import { Dialog } from '../../components/Dialog';
import { Loading, ErrorBox, Empty, InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { CATEGORY_LABEL, categoryOf } from '../../components/TemplatePicker';
import { useMe } from '../../hooks/useMe';

type Filter = 'all' | TemplateCategory;
const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'first_message', label: 'First message' },
  { id: 'reminder', label: 'Reminder' },
  { id: 'other', label: 'Other' },
];

/** First texts and reminders go out in volume, so under 3 wordings is a real problem there. */
function needsThree(c: TemplateCategory): boolean {
  return c === 'first_message' || c === 'reminder';
}

export function TemplatesPage() {
  const q = useQuery({ queryKey: ['templates'], queryFn: api.templates.list });
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<Template | 'new' | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const remove = useMutation({
    mutationFn: (id: string) => api.templates.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast({ text: 'Template deleted.', tone: 'ok' });
    },
    onError: (e) => toast({ text: (e as Error).message, tone: 'error' }),
  });
  const all = q.data?.items ?? [];
  const items = filter === 'all' ? all : all.filter((t) => categoryOf(t) === filter);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="small">
            <Link to="/scheduled">Scheduled</Link> /
          </div>
          <h1>Templates</h1>
        </div>
        <button className="btn primary" onClick={() => setEditing('new')}>
          New template
        </button>
      </div>
      <div className="notice info" style={{ marginBottom: 12 }}>
        Give each template at least 3 wordings. The app rotates them so the same text never goes out over and over, which carriers read as spam. First messages and reminders will not schedule to a list with fewer than 3.
      </div>
      <div className="row wrap" style={{ marginBottom: 12 }}>
        {FILTERS.map((f) => (
          <button key={f.id} className={`btn small ${filter === f.id ? 'primary' : ''}`} onClick={() => setFilter(f.id)}>
            {f.label}
            {f.id !== 'all' && <span className="muted"> {all.filter((t) => categoryOf(t) === f.id).length}</span>}
          </button>
        ))}
      </div>
      <div className="card" style={{ padding: 0 }}>
        {q.isPending && <Loading />}
        {q.isError && (
          <div style={{ padding: 16 }}>
            <ErrorBox error={q.error} retry={() => q.refetch()} />
          </div>
        )}
        {q.isSuccess && items.length === 0 && <Empty title={filter === 'all' ? 'No templates yet' : `No ${CATEGORY_LABEL[filter as TemplateCategory].toLowerCase()} templates`} hint="Make one for appointment reminders or a first text to start." />}
        {items.length > 0 && (
          <ul className="list-plain" style={{ padding: '0 16px' }}>
            {items.map((t) => {
              const cat = categoryOf(t);
              const few = t.variants.length < 3;
              return (
                <li key={t.id}>
                  <div className="row between wrap">
                    <div className="row wrap">
                      <strong>{t.name}</strong>
                      <span className="chip accent">{CATEGORY_LABEL[cat]}</span>
                      <span className={`chip ${few ? (needsThree(cat) ? 'danger' : 'warn') : ''}`}>
                        {t.variants.length} {t.variants.length === 1 ? 'wording' : 'wordings'}
                      </span>
                      {(t.pending_uses ?? t.in_use_pending) ? <span className="muted small">used by {t.pending_uses ?? t.in_use_pending} pending</span> : null}
                    </div>
                    <div className="row">
                      <button className="btn small" onClick={() => setEditing(t)}>
                        Edit
                      </button>
                      <button className="btn small danger" onClick={() => confirm(`Delete “${t.name}”?`) && remove.mutate(t.id)} disabled={remove.isPending}>
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="muted small" style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>
                    {t.variants[0]}
                  </div>
                  {(t.worst_segments ?? 0) >= 2 && (
                    <div className={`tiny ${(t.worst_segments ?? 0) >= 3 ? 'danger-text' : 'warn-text'}`}>
                      Goes out as up to {t.worst_segments} segments once the names are filled in. Shorten the longest wording.
                    </div>
                  )}
                  {few && needsThree(cat) && <div className="tiny danger-text">{CATEGORY_LABEL[cat]} templates need at least 3 wordings before a list can use them.</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {editing && <TemplateEditor template={editing === 'new' ? null : editing} defaultCategory={filter === 'all' ? 'other' : filter} onClose={() => setEditing(null)} />}
    </div>
  );
}

function TemplateEditor({ template, defaultCategory, onClose }: { template: Template | null; defaultCategory: TemplateCategory; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  // WHY (review round 2): the estimate used to fill {{company}} with another dealer's 26-character
  // sample name, so the editor and the saved card showed different numbers.
  const me = useMe();
  const sampleCompany = me.data?.workspace.company_name ?? me.data?.workspace.name ?? undefined;
  const [name, setName] = useState(template?.name ?? '');
  const [category, setCategory] = useState<TemplateCategory>(template ? categoryOf(template) : defaultCategory);
  const [variants, setVariants] = useState<string[]>(template?.variants.length ? [...template.variants] : ['', '', '']);
  const save = useMutation({
    mutationFn: () => {
      const clean = variants.map((v) => v.trim()).filter(Boolean);
      return template ? api.templates.update(template.id, { name: name.trim(), variants: clean, category }) : api.templates.create({ name: name.trim(), variants: clean, category });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      toast({ text: 'Template saved.', tone: 'ok' });
      onClose();
    },
  });
  const filled = variants.filter((v) => v.trim()).length;
  const fewWarning = filled < 3;
  const pending = template?.pending_uses ?? template?.in_use_pending ?? 0;
  const blocksSingle = filled === 1 && pending > 0;
  const placeholder =
    category === 'first_message'
      ? '{{company}}: Hi {{name|there}}, {{tech|our technician}} is on the way and should arrive around {{time}}. Reply here with any questions.'
      : 'Hi {{name|there}}, this is {{company}}. Your appointment is {{date}} at {{time}}. Reply if you need to change it.';

  return (
    <Dialog title={template ? 'Edit template' : 'New template'} onClose={onClose} wide>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <label className="field grow">
          <span>Name</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={category === 'first_message' ? 'On the way' : 'Appointment reminder'} autoFocus />
        </label>
        <label className="field" style={{ minWidth: 180 }}>
          <span>Used for</span>
          <select className="input" value={category} onChange={(e) => setCategory(e.target.value as TemplateCategory)}>
            <option value="first_message">First message</option>
            <option value="reminder">Reminder</option>
            <option value="other">Other</option>
          </select>
        </label>
      </div>
      <div className="field-label">Wordings (the app rotates them)</div>
      {variants.map((v, i) => {
        // WHY (review round 1, trap 5): '{{name|there}}' is 14 characters here but 34 once a real name is in;
        // count the text the way it goes out, with long sample values
        const worst = renderedWorstCase(v, { company: sampleCompany });
        const seg = worst.segments;
        const accented = worst.accentedSegments.segments;
        return (
          <div key={i} className="row" style={{ alignItems: 'flex-start', marginBottom: 8 }}>
            <div className="grow">
              <textarea className="input" value={v} onChange={(e) => setVariants(variants.map((x, j) => (j === i ? e.target.value : x)))} rows={2} placeholder={i === 0 ? placeholder : 'Another way to say the same thing'} />
              {v && (
                <div className={`tiny ${seg.segments >= 3 ? 'danger-text' : seg.segments === 2 ? 'warn-text' : 'muted'}`}>
                  about {seg.length} / {seg.capacity} · {seg.segments} {seg.segments === 1 ? 'segment' : 'segments'} after filling in (long name, long company)
                  {seg.encoding === 'ucs2' && ' · a special character makes this 70-character segments'}
                  {seg.segments >= 2 && ' · shorten it so it stays one segment'}
                  {seg.segments === 1 && accented > 1 && ` · ${accented} segments for a name with an accent (María, José, Raúl)`}
                </div>
              )}
            </div>
            <button className="btn small ghost" onClick={() => setVariants(variants.filter((_, j) => j !== i))} disabled={variants.length <= 1} title="Remove">
              Remove
            </button>
          </div>
        );
      })}
      <button className="btn small" onClick={() => setVariants([...variants, ''])}>
        Add a wording
      </button>
      {fewWarning && (
        <div className={`notice ${needsThree(category) && filled > 0 ? 'danger' : 'warn'}`} style={{ marginTop: 10 }}>
          {filled === 0
            ? 'Write at least one wording.'
            : needsThree(category)
              ? `Only ${filled} ${filled === 1 ? 'wording' : 'wordings'}. ${CATEGORY_LABEL[category]} templates go out in volume, so they need at least 3 before a list can use them.`
              : `Only ${filled} ${filled === 1 ? 'wording' : 'wordings'}. With fewer than 3 the same text repeats and carriers may flag it.`}
        </div>
      )}
      {blocksSingle && <div className="notice danger" style={{ marginTop: 10 }}>Pending reminders use this template, so it cannot drop to a single wording.</div>}
      <div className="notice" style={{ marginTop: 10 }}>
        <strong>Variables:</strong> {'{{name}}'} the contact's name · {'{{date}}'} appointment day · {'{{time}}'} appointment or arrival time · {'{{tech}}'} technician · {'{{company}}'} your company name.
        <br />
        Write {'{{name|there}}'} to use “there” when the contact has no name, {'{{tech|our technician}}'} when no name is given.
      </div>
      <InlineError error={save.error} />
      <div className="actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!name.trim() || filled === 0 || blocksSingle || save.isPending} onClick={() => save.mutate()}>
          Save
        </button>
      </div>
    </Dialog>
  );
}
